import { ImageWeb } from '@nucleus/types/media/image';
import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import { IconCirclePlay } from '../icons/IconCirclePlay';
import { cornerStyleInterpolationFactory } from '../lib/cornerStyle';
import { formatSrcSet } from '../lib/media';
import { Thumbnail } from './Thumbnail';

declare module '@nucleus/sermon-theme-elements' {
  interface SermonThemeElementsTheme {
    SermonPreview?: {
      background?: string;
    };
  }
}

export interface SermonPreviewProps {
  children?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  byline?: React.ReactNode;
  videoUrl: string;
  image?: ImageWeb;
}

export const SermonPreview = ({ title, description, byline, videoUrl, image }: SermonPreviewProps): JSX.Element => {
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const handlePlay = () => {
    setIsPlayerVisible(true);
    setIsVideoPlaying(true);
  };

  return (
    <Container>
      <MediaContainer>
        {isPlayerVisible ? (
          <VideoContainer>
            <StyledReactPlayer
              src={videoUrl}
              width="100%"
              height="100%"
              controls={true}
              playing={isVideoPlaying}
              playsInline={true}
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
            />
          </VideoContainer>
        ) : (
          <PosterContainer onClick={handlePlay}>
            <Thumbnail src={image?.src} srcSet={formatSrcSet(image?.srcSet)} blurHash={image?.blurHash} />
            <LinearGradient />
            <PlayButton />
          </PosterContainer>
        )}
      </MediaContainer>
      <MetaContainer>
        {title}
        {description}
        {byline}
      </MetaContainer>
    </Container>
  );
};

const LinearGradient = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(0deg, #000000, transparent);
  opacity: 0;
  transition: opacity 900ms cubic-bezier(0.23, 1, 0.32, 1);
`;

const Container = styled.div`
  display: block;
  margin: 1.5em 0;
  max-width: 828px;
  height: 100%; /* 716px */
  width: 100%;
  background: ${({ theme }) => theme._sermonThemeElements.SermonPreview?.background};
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '18px',
    pill: '40px',
  })};
  padding: min(58px, calc(var(--unit-length) * 2)) min(58px, calc(var(--unit-length) * 2));
`;

const MediaContainer = styled.div`
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '10px',
    pill: '18px',
  })};
  box-shadow: 24px 24px 52px 0 rgba(0, 0, 0, 0.24);
  overflow: hidden;
`;

const PosterContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${cornerStyleInterpolationFactory({
    square: '0px',
    rounded: '10px',
    pill: '18px',
  })};
  overflow: hidden;
  cursor: pointer;

  &:hover {
    ${LinearGradient} {
      opacity: 0.9;
    }
  }
`;

const PlayButton = styled(IconCirclePlay)`
  width: 14%;
  position: absolute;
  pointer-events: none;
  transition: transform 300ms cubic-bezier(0.23, 1, 0.32, 1);

  ${PosterContainer}:hover & {
    transform-origin: center;
    transform: scale(1.1);
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  height: 0;
  padding-bottom: 56.25%;
`;

const StyledReactPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
`;

const MetaContainer = styled.div`
  padding-top: 1em;
  padding-left: calc(var(--unit-length) * 0.6);
`;
